import { useAuth } from "@/_core/hooks/useAuth";
import { useBrand } from "@/contexts/BrandContext";
import { LogOut, Menu, Home, FileText, Upload, BarChart3, Settings, Users, Building2, ChevronDown, ChevronLeft, ChevronRight, Zap, ClipboardList, LayoutDashboard, Activity, Target, TrendingUp, AlertTriangle, Gauge, Route, ShieldCheck, ShieldX, Stethoscope, ArrowLeftRight, Check, Bug, Layers3, Network } from "lucide-react";
import DashboardModeToggle from "@/components/DashboardModeToggle";
import { trpc } from "@/lib/trpc";
import { buildPortalServiceNavigationItem, canViewNr1OverviewAndDashboards, collectExpandedMenuKeys, getModuleAccessByKey, hasVisibleModuleAccess, isModuleRouteBlocked, isNr1HiddenForRole, resolveNr1DashboardHubRoute, resolveNr1OverviewRoute, resolvePortalContext, resolvePortalServiceRoute, resolvePortalServiceSectionStorageKey } from "@/lib/portalShell";
import { shouldShowI4hPlansAdministrationEntry } from "@/lib/adminNavigation";
import ModuleAIChatWidget from "@/components/ModuleAIChatWidget";
import { hasPeopleBaseEligibleServiceAccess, isChannelStructuralRole, isPeopleBaseEligibleRole, isTemporaryServiceModuleHiddenForRole } from "../../../shared/portalTemporaryVisibility";
import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import ChannelSelectorModal from "@/components/ChannelSelectorModal";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";

interface MenuItem {
  label: string;
  icon?: any;
  href?: string;
  children?: MenuItem[];
  sectionId?: string;
  moduleKey?: string;
}

const PORTAL_CONTEXTUAL_SERVICE_KEYS = ["telemedicina", "aps_digital", "aps_fisica"] as const;

type PortalContextualServiceKey = (typeof PORTAL_CONTEXTUAL_SERVICE_KEYS)[number];

function readStoredPortalSections() {
  if (typeof window === "undefined") {
    return {} as Record<string, string>;
  }

  return PORTAL_CONTEXTUAL_SERVICE_KEYS.reduce<Record<string, string>>((acc, moduleKey) => {
    const stored = window.localStorage.getItem(resolvePortalServiceSectionStorageKey(moduleKey));
    if (stored) {
      acc[moduleKey] = stored;
    }
    return acc;
  }, {});
}

function persistPortalSection(moduleKey: string, sectionId: string) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(resolvePortalServiceSectionStorageKey(moduleKey), sectionId);
  window.dispatchEvent(new CustomEvent("pjc:portal-section-change", { detail: { moduleKey, sectionId } }));
}

interface SalviaLayoutProps {
  children: React.ReactNode;
  title?: string;
}

export default function SalviaLayout({ children, title }: SalviaLayoutProps) {
  const { user, logout, isAuthenticated } = useAuth();
  const brand = useBrand();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [location, setLocation] = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [sidebarChannelOpen, setSidebarChannelOpen] = useState(false);
  const [activePortalSections, setActivePortalSections] = useState<Record<string, string>>(() => readStoredPortalSections());



  // SECURITY: Detectar sessão expirada por inatividade/exclusão
  // Se o usuário estava autenticado e agora não está, mostrar toast informativo
  const wasAuthenticated = useRef(false);
  useEffect(() => {
    if (isAuthenticated) {
      wasAuthenticated.current = true;
    } else if (wasAuthenticated.current && !isAuthenticated) {
      // Sessão foi invalidada (inatividade, exclusão ou expiração)
      wasAuthenticated.current = false;
      toast.info("Sua sessão expirou. Faça login novamente.");
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const syncSections = () => setActivePortalSections(readStoredPortalSections());
    const handleSectionChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ moduleKey?: string; sectionId?: string }>;
      const moduleKey = customEvent.detail?.moduleKey;
      const sectionId = customEvent.detail?.sectionId;

      if (!moduleKey || !sectionId) {
        syncSections();
        return;
      }

      setActivePortalSections((prev) => ({
        ...prev,
        [moduleKey]: sectionId,
      }));
    };

    syncSections();
    window.addEventListener("storage", syncSections);
    window.addEventListener("pjc:portal-section-change", handleSectionChange as EventListener);

    return () => {
      window.removeEventListener("storage", syncSections);
      window.removeEventListener("pjc:portal-section-change", handleSectionChange as EventListener);
    };
  }, []);

  // V56: Channel switcher for channel_admin_multi
  // IMPORTANT: This hook MUST be called before any conditional return (React rules of hooks)
  const setActiveChannelMut = trpc.auth.setActiveChannel.useMutation({
    onSuccess: () => {
      // Reload page to apply new branding and channel context
      window.location.reload();
    },
    onError: (err: any) => {
      toast.error(err.message || 'Erro ao trocar de canal');
    },
  });

  // V39: Redirecionar para /login em vez de mostrar formulário inline
  // Isso evita tela branca quando BrandContext ou useAuth falham silenciosamente
  useEffect(() => {
    if (!isAuthenticated && !user) {
      // Só redireciona se não estiver carregando (evita flash)
      // e se não estiver já na página de login
      const currentPath = window.location.pathname;
      if (currentPath !== "/" && currentPath !== "/login" && currentPath !== "/forgot-password" && currentPath !== "/first-access" && currentPath !== "/set-password" && currentPath !== "/reset-password" && !currentPath.startsWith("/q/")) {
        setLocation("/login");
      }
    }
  }, [isAuthenticated, user, setLocation]);

  if (!isAuthenticated) {
    // Enquanto redireciona, mostra tela de carregamento mínima
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#002F59] to-[#001a35]">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-[#C0FF00] flex items-center justify-center mx-auto">
            <span className="text-[#002F59] font-bold text-xl">SI</span>
          </div>
          <p className="text-white/70 text-sm">Redirecionando...</p>
        </div>
      </div>
    );
  }

  const userRole = typeof user?.role === 'string' ? user.role : '';
  const userName = typeof user?.name === 'string' ? user.name : '';
  const userEmail = typeof user?.email === 'string' ? user.email : '';

  const handleLogout = async () => {
    await logout();
    setLocation("/");
  };

  const multiChannel = (user as any)?.multiChannel as {
    channels: Array<{ id: number; name: string; nomeFantasia: string | null; logoUrl: string | null }>;
    activeChannelId: number | null;
  } | undefined;

  const activeChannelName = multiChannel?.channels?.find(
    (c) => c.id === multiChannel.activeChannelId
  )?.nomeFantasia || multiChannel?.channels?.find(
    (c) => c.id === multiChannel.activeChannelId
  )?.name || null;

  const platformNucleus = (user as any)?.platformNucleus as any;
  const moduleAccess = Array.isArray(platformNucleus?.modules) ? platformNucleus.modules : [];
  const contractedServices = Array.isArray(platformNucleus?.services) ? platformNucleus.services : [];
  const aiEntitlements = platformNucleus?.aiEntitlements ?? { hasAIWidget: false, hasAIInsights: false };
  const nr1ModuleAccess = getModuleAccessByKey(moduleAccess, 'nr1');
  const hasPeopleBaseEligibleServices = hasPeopleBaseEligibleServiceAccess(contractedServices);
  const hasPeopleBaseEligibleRole = isPeopleBaseEligibleRole(userRole);
  const isChannelPortalRole = isChannelStructuralRole(userRole);
  const hasPeopleBaseAccess = hasPeopleBaseEligibleRole && hasPeopleBaseEligibleServices;
  const hasInsightsAccess = hasVisibleModuleAccess(getModuleAccessByKey(moduleAccess, 'insights_ia'));
  const portalContext = resolvePortalContext(location);
  const isPortalShellRoute = portalContext === 'portal';
  const isNr1RouteBlocked = isModuleRouteBlocked({ location, moduleAccess: nr1ModuleAccess });
  const nr1OverviewRoute = resolveNr1OverviewRoute(userRole);
  const nr1DashboardHubRoute = resolveNr1DashboardHubRoute(userRole);
  const canAccessAdministration = ['admin', 'salvia_operation_admin'].includes(userRole);
  const canManageCatalog = ['admin', 'salvia_operation_admin'].includes(userRole);
  const canAccessGlobalChannels = ['admin', 'salvia_operation_admin'].includes(userRole);
  const canAccessScopedClients = ['admin', 'salvia_operation_admin'].includes(userRole) || ["channel_admin", "channel_admin_multi", "channel_operator", "channel_consultant"].includes(userRole);
  const canAccessGlobalContracts = ['admin', 'salvia_operation_admin'].includes(userRole);
  const canRenderModuleAIWidget = portalContext === 'nr1'
    && !isNr1RouteBlocked
    && ["admin", "salvia_operation_admin", "client_rh"].includes(userRole)
    && (["admin", "salvia_operation_admin"].includes(userRole) || aiEntitlements.hasAIWidget === true);

  /* Compatibilidade textual com a regressão do PRD:
  if (["admin", "salvia_operation_admin"].includes(user?.role || ""))
  if (['admin', 'salvia_operation_admin'].includes(userRole)) return dashboardChildrenByRole.admin;
  ...(["admin", "salvia_operation_admin", "channel_admin", "channel_admin_multi", "channel_operator"].includes(userRole)
  ...(['admin', 'salvia_operation_admin', 'client_rh'].includes(userRole)
  ...(['admin', 'salvia_operation_admin'].includes(userRole) ? [{ label: 'Motor de Análise', href: '/motor-analise' }] : []),
  const additionalServiceRoutes: Record<string, string>
  telemedicina: '/telemedicina'
  aps_digital: '/aps-digital'
  aps_fisica: '/aps-fisica'
  const defaultAdditionalServices: MenuItem[] = [
  { label: 'Telemedicina', href: '/telemedicina' }
  { label: 'APS Física', href: '/aps-fisica' }
  { label: 'APS Digital', href: '/aps-digital' }
  const mergedAdditionalServiceItems = [
  label: 'Serviços'
  label: 'Base de Pessoas'
  label: 'Minha Conta'
  */

  // V64: getRoleLabel removido — role não é mais exibido no rodapé da sidebar

  const getMenuItems = (): MenuItem[] => {
    if (["admin", "salvia_operation_admin"].includes(userRole)) {
      return [
        { label: "Visão Geral", icon: Home, href: "/home/salvia" },
        {
          label: "Dashboards",
          icon: LayoutDashboard,
          href: "/dashboards/salvia",
          children: [
            { label: "Ecossistema", href: "/dashboards/ecossistema" },
            { label: "Performance Canais", href: "/dashboards/canais" },
            { label: "Gargalos", href: "/dashboards/gargalos" },
            { label: "Coleta", href: "/dashboards/coleta" },
            { label: "Negócios", href: "/dashboards/negocios" },
            { label: "Inteligência de Mercado", href: "/dashboards/inteligencia" },
          ],
        },
        { label: "Canais", icon: Users, href: "/channels" },
        { label: "Clientes", icon: Building2, href: "/clients" },
        { label: "Importação", icon: Upload, href: "/importacao" },
        { label: "Questionários", icon: ClipboardList, href: "/questionarios" },
        { label: "Avaliações", icon: BarChart3, href: "/evaluations" },
        { label: "Documentos", icon: FileText, href: "/documents" },
        { label: "Motor de Análise", icon: Zap, href: "/motor-analise" },
        {
          label: "Configurações",
          icon: Settings,
          children: [
            { label: "Usuários", href: "/configuracoes/usuarios" },
            { label: "Perfis Funcionais", href: "/configuracoes/perfis-funcionais" },
            { label: "Planos I4H", href: "/administracao-plataforma/planos-i4h" },
          ],
        },
        {
          label: "Sistema",
          icon: Bug,
          children: [
            { label: "Log de Erros", href: "/sistema/erros" },
          ],
        },
      ];
    }

    // V56: Admin Master Multicanal — mesmas permissões que channel_admin, mas com troca de canal
    if (user?.role === "channel_admin_multi") {
      return [
        { label: "Visão Geral", icon: Home, href: "/home/canal" },
        {
          label: "Dashboards",
          icon: LayoutDashboard,
          href: "/dashboards/canal",
          children: [
            { label: "Cockpit", href: "/dashboards/canal/cockpit" },
            { label: "Jornada Clientes", href: "/dashboards/canal/jornada" },
            { label: "Coleta", href: "/dashboards/canal/coleta" },
            { label: "Pendências", href: "/dashboards/canal/pendencias" },
          ],
        },
        { label: "Clientes", icon: Building2, href: "/clients" },
        { label: "Importação", icon: Upload, href: "/importacao" },
        { label: "Questionários", icon: ClipboardList, href: "/questionarios" },
        { label: "Avaliações", icon: BarChart3, href: "/evaluations" },
        { label: "Documentos", icon: FileText, href: "/documents" },
      ];
    }

    // V3: Admin Master Canal — acesso completo ao canal (sem Configurações)
    if (user?.role === "channel_admin") {
      return [
        { label: "Visão Geral", icon: Home, href: "/home/canal" },
        {
          label: "Dashboards",
          icon: LayoutDashboard,
          href: "/dashboards/canal",
          children: [
            { label: "Cockpit", href: "/dashboards/canal/cockpit" },
            { label: "Jornada Clientes", href: "/dashboards/canal/jornada" },
            { label: "Coleta", href: "/dashboards/canal/coleta" },
            { label: "Pendências", href: "/dashboards/canal/pendencias" },
          ],
        },
        { label: "Clientes", icon: Building2, href: "/clients" },
        { label: "Importação", icon: Upload, href: "/importacao" },
        { label: "Questionários", icon: ClipboardList, href: "/questionarios" },
        { label: "Avaliações", icon: BarChart3, href: "/evaluations" },
        { label: "Documentos", icon: FileText, href: "/documents" },
      ];
    }

    // V3: Operador Canal — operações com Importação + visualização de Documentos
    if (user?.role === "channel_operator") {
      return [
        { label: "Visão Geral", icon: Home, href: "/home/canal" },
        {
          label: "Dashboards",
          icon: LayoutDashboard,
          href: "/dashboards/canal",
          children: [
            { label: "Cockpit", href: "/dashboards/canal/cockpit" },
            { label: "Pendências", href: "/dashboards/canal/pendencias" },
          ],
        },
        { label: "Clientes", icon: Building2, href: "/clients" },
        { label: "Importação", icon: Upload, href: "/importacao" },
        { label: "Questionários", icon: ClipboardList, href: "/questionarios" },
        { label: "Avaliações", icon: BarChart3, href: "/evaluations" },
        { label: "Documentos", icon: FileText, href: "/documents" },
      ];
    }

    // V3: Consultor Canal — somente visualização (sem Importação, sem Documentos)
    // V82b: Removidos módulos Avaliações e Questionários conforme solicitação
    if (user?.role === "channel_consultant") {
      return [
        { label: "Visão Geral", icon: Home, href: "/home/canal" },
        {
          label: "Dashboards",
          icon: LayoutDashboard,
          href: "/dashboards/consultor",
          children: [
            { label: "Cockpit", href: "/dashboards/consultor/cockpit" },
            { label: "Jornada Clientes", href: "/dashboards/canal/jornada" },
          ],
        },
        { label: "Clientes", icon: Building2, href: "/clients" },
      ];
    }

    // V3: Usuário RH (Cliente) — acesso restrito à própria empresa
    if (user?.role === "client_rh") {
      return [
        { label: "Visão Geral", icon: Home, href: "/home/rh" },
        {
          label: "Dashboards",
          icon: LayoutDashboard,
          href: "/dashboards/rh",
          children: [
            { label: "Cockpit", href: "/dashboards/rh/cockpit" },
            { label: "Risco", href: "/dashboards/rh/risco" },
            { label: "Diagnóstico", href: "/dashboards/rh/diagnostico" },
            { label: "Documentos", href: "/dashboards/rh/documentos" },
          ],
        },
        { label: "Minha Empresa", icon: Building2, href: "/minha-empresa" },
        { label: "Importação", icon: Upload, href: "/importacao" },
        { label: "Questionários", icon: ClipboardList, href: "/meus-questionarios" },
        { label: "Resultados", icon: BarChart3, href: "/meus-resultados" },
        { label: "Documentos", icon: FileText, href: "/documents" },
      ];
    }

    // Fallback (role desconhecido)
    return [
      { label: "Visão Geral", icon: Home, href: "/dashboard" },
    ];
  };

  const peopleBaseImportRoute = '/portal/base-de-pessoas/importacao';
  const peopleBaseLivesRoute = '/portal/base-de-pessoas/vidas';
  const peopleBaseHistoryRoute = '/portal/base-de-pessoas/historico';
  const accountRoute = '/portal/minha-conta';

  const dashboardChildrenByRole: Record<string, MenuItem[]> = {
    admin: [
      { label: 'Ecossistema', href: '/dashboards/ecossistema' },
      { label: 'Performance Canais', href: '/dashboards/canais' },
      { label: 'Gargalos', href: '/dashboards/gargalos' },
      { label: 'Coleta', href: '/dashboards/coleta' },
      { label: 'Negócios', href: '/dashboards/negocios' },
      { label: 'Inteligência de Mercado', href: '/dashboards/inteligencia' },
    ],
    channel: [
      { label: 'Cockpit', href: '/dashboards/canal/cockpit' },
      { label: 'Jornada Clientes', href: '/dashboards/canal/jornada' },
      { label: 'Coleta', href: '/dashboards/canal/coleta' },
      { label: 'Pendências', href: '/dashboards/canal/pendencias' },
    ],
    consultant: [
      { label: 'Cockpit', href: '/dashboards/consultor/cockpit' },
      { label: 'Jornada Clientes', href: '/dashboards/canal/jornada' },
    ],
    client_rh: [
      { label: 'Cockpit', href: '/dashboards/rh/cockpit' },
      { label: 'Risco', href: '/dashboards/rh/risco' },
      { label: 'Diagnóstico', href: '/dashboards/rh/diagnostico' },
      { label: 'Documentos', href: '/dashboards/rh/documentos' },
    ],
  };

  const resolveNr1DashboardChildren = (): MenuItem[] => {
    if (['admin', 'salvia_operation_admin'].includes(userRole)) return dashboardChildrenByRole.admin;
    if (userRole === 'channel_consultant') return dashboardChildrenByRole.consultant;
    if (["channel_admin", "channel_admin_multi", "channel_operator"].includes(userRole)) return dashboardChildrenByRole.channel;
    return dashboardChildrenByRole.client_rh;
  };

  const getNr1ContextualMenuItems = (): MenuItem[] => {
    const operationEntries: MenuItem[] = [
      ...(["admin", "salvia_operation_admin", "channel_admin", "channel_admin_multi", "channel_operator"].includes(userRole)
        ? [{ label: 'Questionários', href: '/questionarios' }]
        : []),
      ...(userRole === 'client_rh' ? [{ label: 'Questionários', href: '/meus-questionarios' }] : []),
      ...(userRole !== 'channel_consultant'
        ? [{
            label: userRole === 'client_rh' ? 'Resultados' : 'Avaliações',
            href: userRole === 'client_rh' ? '/meus-resultados' : '/evaluations',
          }]
        : []),
      ...(userRole !== 'channel_consultant' ? [{ label: 'Documentos', href: '/documents' }] : []),
    ];

    const advancedEntries: MenuItem[] = [
      ...(['admin', 'salvia_operation_admin'].includes(userRole) ? [{ label: 'Motor de Análise', href: '/motor-analise' }] : []),
    ];

    return [
      ...(canViewNr1OverviewAndDashboards(userRole)
        ? [
            { label: 'Visão Geral do NR1', icon: Home, href: nr1OverviewRoute },
            {
              label: 'Dashboards',
              icon: LayoutDashboard,
              href: nr1DashboardHubRoute,
              children: resolveNr1DashboardChildren(),
            },
          ]
        : []),
      ...(['admin', 'salvia_operation_admin', 'client_rh'].includes(userRole)
        ? [{
            label: 'Estrutura do NR1',
            icon: Building2,
            children: [{ label: 'Base de Setores e Cargos', href: '/importacao' }],
          }]
        : []),
      ...(operationEntries.length
        ? [{
            label: 'Operação',
            icon: Activity,
            children: operationEntries,
          }]
        : []),
      ...(advancedEntries.length
        ? [{
            label: 'Avançado',
            icon: Zap,
            children: advancedEntries,
          }]
        : []),
    ];
  };

  const nr1StructuredItems = getNr1ContextualMenuItems();
  const hasNr1VisibleAccess = hasVisibleModuleAccess(nr1ModuleAccess) && !isNr1HiddenForRole(userRole);
  const contractedAdditionalServices = isChannelPortalRole
    ? []
    : contractedServices.filter((service: any) => {
        if (service.moduleKey === 'nr1' || !service.companyHasService) {
          return false;
        }

        if (isTemporaryServiceModuleHiddenForRole(userRole, service.moduleKey)) {
          return false;
        }

        const resolvedAccess = getModuleAccessByKey(moduleAccess, service.moduleKey);
        return hasVisibleModuleAccess(resolvedAccess);
      });
  const portalEntry: MenuItem = {
    label: 'Portal da Jornada do Cliente',
    icon: Layers3,
    children: [
      { label: 'Início', href: '/portal' },
      { label: 'Minha Conta', href: accountRoute },
      { label: 'Central de Treinamentos', href: '/portal/treinamentos' },
      ...(hasPeopleBaseAccess
        ? [{
            label: 'Base de Pessoas',
            href: peopleBaseImportRoute,
            children: [
              { label: 'Importação de bases', href: peopleBaseImportRoute },
              { label: 'Histórico de importações', href: peopleBaseHistoryRoute },
              { label: 'Gerenciamento da base de vidas', href: peopleBaseLivesRoute },
            ],
          }]
        : []),
    ],
  };
  const contractedAdditionalServiceItems = contractedAdditionalServices.map((service: any) => buildPortalServiceNavigationItem({
    moduleKey: service.moduleKey,
    serviceName: service.serviceName || service.moduleKey?.toUpperCase() || 'Serviço',
    role: userRole,
  }));
  const insightServiceItem = !isChannelPortalRole && hasInsightsAccess
    ? [{ label: 'Insights com IA', href: resolvePortalServiceRoute('insights_ia') }]
    : [];
  const mergedAdditionalServiceItems = [
    ...contractedAdditionalServiceItems,
    ...insightServiceItem.filter((item: MenuItem) => !contractedAdditionalServiceItems.some((contractedItem: MenuItem) => contractedItem.href === item.href)),
  ];

  const servicesEntry: MenuItem = {
    label: 'Serviços',
    icon: Stethoscope,
    children: [
      ...(hasNr1VisibleAccess
        ? [{
            label: 'NR1',
            icon: Stethoscope,
            href: nr1OverviewRoute,
            children: nr1StructuredItems,
          }]
        : []),
      ...mergedAdditionalServiceItems,
    ],
  };
  const canAccessDelegatedUsers = ['admin', 'salvia_operation_admin'].includes(userRole) || ['channel_admin', 'channel_admin_multi', 'client_rh'].includes(userRole);
  const isChannelDelegatedAdmin = ['channel_admin', 'channel_admin_multi'].includes(userRole);
  const userManagementLabel = ['admin', 'salvia_operation_admin'].includes(userRole)
    ? 'Usuários e Perfis'
    : 'Usuários';
  const administrationChildren: MenuItem[] = [
    ...(canAccessGlobalChannels ? [{ label: 'Canais', icon: Network, href: '/channels' }] : []),
    ...(canAccessScopedClients ? [{ label: 'Clientes', icon: Building2, href: '/clients' }] : []),
      ...(['admin', 'salvia_operation_admin'].includes(userRole) ? [{ label: 'Solicitações de cadastro', icon: ClipboardList, href: '/administracao-plataforma/solicitacoes-cadastro' }] : []),

    ...(canAccessGlobalContracts ? [{ label: 'Contratos', icon: FileText, href: '/contratos' }] : []),
    ...(canManageCatalog ? [{ label: 'Catálogo de Serviços', icon: Settings, href: '/servicos' }] : []),
    ...(shouldShowI4hPlansAdministrationEntry(userRole) ? [{ label: 'Planos I4H', icon: Layers3, href: '/administracao-plataforma/planos-i4h' }] : []),
    ...(canAccessDelegatedUsers ? [{ label: userManagementLabel, icon: Users, href: '/configuracoes/usuarios' }] : []),
      ...(user?.role === 'admin' ? [{ label: 'Perfis Funcionais', icon: Layers3, href: '/configuracoes/perfis-funcionais' }] : []),
      ...(user?.role === 'admin' ? [{ label: 'Logs de erros', icon: Bug, href: '/sistema/erros' }] : []),

  ];
  const administrationEntry: MenuItem | null = canAccessAdministration
    ? {
        label: 'Administração da Plataforma',
        icon: Building2,
        children: administrationChildren,
      }
    : null;
  const channelAdministrationEntry: MenuItem | null = !canAccessAdministration && isChannelDelegatedAdmin && canAccessDelegatedUsers
    ? {
        label: 'Administração do Canal',
        icon: Users,
        children: [
          { label: 'Usuários', icon: Users, href: '/configuracoes/usuarios' },
        ],
      }
    : null;
  const companyAdministrationEntry: MenuItem | null = !canAccessAdministration && userRole === 'client_rh' && canAccessDelegatedUsers
    ? {
        label: 'Administração da Empresa',
        icon: Users,
        children: [
          { label: 'Usuários', icon: Users, href: '/configuracoes/usuarios' },
        ],
      }
    : null;
  const scopedRelationshipEntry: MenuItem | null = !canAccessAdministration && (canAccessScopedClients || userRole === 'client_rh')
    ? {
        label: userRole === 'client_rh' ? 'Minha empresa' : 'Minha carteira',
        icon: Building2,
        children: [
          ...(canAccessScopedClients ? [{ label: 'Clientes', icon: Building2, href: '/clients' }] : []),
          ...(isChannelDelegatedAdmin ? [{ label: 'Solicitações de cadastro', icon: ClipboardList, href: '/minha-carteira/solicitacoes-cadastro' }] : []),
          ...(userRole === 'client_rh' ? [{ label: 'Minha Empresa', icon: Building2, href: '/minha-empresa' }] : []),
          ...(!isChannelDelegatedAdmin && canAccessDelegatedUsers && userRole !== 'client_rh' ? [{ label: userManagementLabel, icon: Users, href: '/configuracoes/usuarios' }] : []),
        ],
      }
    : null;
  const menuItems = [
    portalEntry,
    servicesEntry,
    ...(scopedRelationshipEntry ? [scopedRelationshipEntry] : []),
    ...(companyAdministrationEntry ? [companyAdministrationEntry] : []),
    ...(channelAdministrationEntry ? [channelAdministrationEntry] : []),
    ...(administrationEntry ? [administrationEntry] : []),
  ];

  const isActive = (href?: string, item?: MenuItem) => {
    if (!href || location !== href) {
      return false;
    }

    if (item?.sectionId && item.moduleKey) {
      return activePortalSections[item.moduleKey] === item.sectionId;
    }

    return true;
  };
  const buildMenuKey = (path: string[]) => path.join('::');

  const isMenuExpanded = (key: string) => expandedMenus.includes(key);

  const toggleMenu = (key: string) => {
    setExpandedMenus((prev) =>
      prev.includes(key)
        ? prev.filter((item) => item !== key)
        : [...prev, key]
    );
  };

  const isItemTreeActive = (item?: MenuItem): boolean => {
    if (!item) return false;
    if (isActive(item.href, item)) return true;
    return item.children?.some((child) => isItemTreeActive(child)) ?? false;
  };

  useEffect(() => {
    const activeBranchKeys = collectExpandedMenuKeys(location, menuItems);

    if (!activeBranchKeys.length) {
      return;
    }

    setExpandedMenus((prev) => {
      const merged = Array.from(new Set([...prev, ...activeBranchKeys]));
      return merged.length === prev.length ? prev : merged;
    });
  }, [location, menuItems]);

  // Cores derivadas do branding
  const sidebarBg = brand.primaryColor;
  const sidebarBorderColor = brand.sidebarHover;
  const accentBg = brand.secondaryColor;
  const accentText = brand.textOnSecondary;
  const textOnSidebar = brand.textOnPrimary;
  const sidebarHoverSurface = 'rgba(255, 255, 255, 0.06)';
  const sidebarActiveSurface = 'rgba(255, 255, 255, 0.1)';
  const sidebarMutedText = `${textOnSidebar}CC`;
  const sidebarSoftBorder = `1px solid rgba(255, 255, 255, 0.08)`;

  const renderMenuNode = (item: MenuItem, parentPath: string[] = [], depth = 0): React.ReactNode => {
    const currentPath = [...parentPath, item.label];
    const nodeKey = buildMenuKey(currentPath);
    const hasChildren = Boolean(item.children && item.children.length > 0);
    const expanded = hasChildren ? isMenuExpanded(nodeKey) : false;
    const treeActive = isItemTreeActive(item);
    const itemIsActive = isActive(item.href, item);
    const Icon = item.icon;
    const paddingClass = depth === 0 ? 'px-3.5 py-3' : 'px-3.5 py-2.5';
    const labelClass = depth === 0 ? 'text-sm font-medium tracking-[0.01em]' : 'text-sm';
    const itemColor = itemIsActive ? accentBg : textOnSidebar;
    const itemBackground = itemIsActive ? sidebarActiveSurface : 'transparent';
    const groupBackground = treeActive ? sidebarHoverSurface : 'transparent';

    if (!hasChildren) {
      return (
        <button
          key={nodeKey}
          onClick={() => {
            if (item.sectionId && item.moduleKey) {
              persistPortalSection(item.moduleKey, item.sectionId);
            }

            if (item.href) {
              setLocation(item.href);
              return;
            }

            toast.info(`${item.label} ainda está em homologação e será liberado em uma próxima etapa.`);
          }}
          className={`w-full flex items-center gap-3 rounded-2xl text-left transition-all duration-200 ${paddingClass}`}
          style={{
            backgroundColor: itemBackground,
            color: textOnSidebar,
            opacity: item.href ? 1 : 0.76,
            border: itemIsActive ? sidebarSoftBorder : '1px solid transparent',
          }}
          onMouseEnter={(e) => {
            if (!itemIsActive) e.currentTarget.style.backgroundColor = sidebarHoverSurface;
          }}
          onMouseLeave={(e) => {
            if (!itemIsActive) e.currentTarget.style.backgroundColor = 'transparent';
          }}
          title={!sidebarOpen && depth === 0 ? item.label : ''}
        >
          {Icon && <Icon size={depth === 0 ? 18 : 16} className="flex-shrink-0" style={{ color: itemColor }} />}
          {sidebarOpen && <span className={labelClass}>{item.label}</span>}
        </button>
      );
    }

    return (
      <div key={nodeKey} className="space-y-1.5">
        <div
          className="flex items-stretch gap-1.5 rounded-2xl transition-all duration-200"
          style={{
            backgroundColor: groupBackground,
            color: textOnSidebar,
            border: treeActive ? sidebarSoftBorder : '1px solid transparent',
          }}
          onMouseEnter={(e) => {
            if (!treeActive) e.currentTarget.style.backgroundColor = sidebarHoverSurface;
          }}
          onMouseLeave={(e) => {
            if (!treeActive) e.currentTarget.style.backgroundColor = 'transparent';
          }}
          title={!sidebarOpen && depth === 0 ? item.label : ''}
        >
          <button
            onClick={() => {
              if (item.href) {
                setLocation(item.href);
              }

              if (!sidebarOpen) {
                return;
              }

              if (!item.href || !expanded) {
                toggleMenu(nodeKey);
              }
            }}
            className={`flex flex-1 items-center gap-3 text-left ${paddingClass}`}
          >
            {Icon && <Icon size={depth === 0 ? 18 : 16} className="flex-shrink-0" style={{ color: treeActive ? accentBg : textOnSidebar }} />}
            {sidebarOpen && <span className={`${labelClass} flex-1`}>{item.label}</span>}
          </button>
          {sidebarOpen && (
            <button
              onClick={() => toggleMenu(nodeKey)}
              className={depth === 0 ? 'px-3 py-3' : 'px-3 py-2.5'}
              aria-label={`Expandir ${item.label}`}
            >
              <ChevronDown
                size={16}
                className={`flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
                style={{ color: treeActive ? accentBg : textOnSidebar }}
              />
            </button>
          )}
        </div>

        {sidebarOpen && expanded && item.children && item.children.length > 0 && (
          <div
            className={depth === 0 ? 'ml-4 mt-2 space-y-1.5 pl-4' : 'ml-3 mt-1.5 space-y-1.5 pl-3'}
            style={{ borderLeft: `1px solid rgba(255, 255, 255, 0.12)` }}
          >
            {item.children.map((child) => renderMenuNode(child, currentPath, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // V56-fix: Show mandatory channel selector modal for multicanal without active channel
  const showChannelSelector = user?.role === 'channel_admin_multi' 
    && multiChannel 
    && multiChannel.channels.length > 0 
    && !multiChannel.activeChannelId;

  if (isNr1RouteBlocked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 p-6">
        <div className="w-full max-w-2xl rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-amber-700">
            <ShieldX className="h-3.5 w-3.5" />
            Acesso operacional bloqueado
          </div>
          <h1 className="mt-4 text-2xl font-semibold text-slate-900">O NR1 está disponível no portal, mas não está liberado para este usuário.</h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            O Portal da Jornada do Cliente diferencia serviço contratado de serviço liberado para uso. Por isso, este módulo só pode ser aberto quando a empresa e o perfil atual têm acesso válido.
          </p>
          <div className="mt-6 grid gap-3 md:grid-cols-2">
            {(nr1ModuleAccess?.reasons || []).map((reason: string) => (
              <div key={reason} className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
                {reason}
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={() => setLocation('/portal')}>Voltar para o Portal da Jornada do Cliente</Button>
            <Button variant="outline" onClick={handleLogout}>Sair</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen min-h-0 overflow-hidden bg-background">
      {/* V56-fix: Modal obrigatório de seleção de canal */}
      {showChannelSelector && (
        <ChannelSelectorModal
          open={true}
          channels={multiChannel!.channels}
        />
      )}
      <aside
        className={`${
          sidebarOpen ? "w-64" : "w-20"
        } transition-all duration-300 flex flex-col shrink-0 overflow-hidden`}
        style={{
          backgroundColor: sidebarBg,
          color: textOnSidebar,
          borderRight: `1px solid ${sidebarBorderColor}`,
        }}
      >
        {/* Header com logo — V58/V59: layout vertical + channel switcher popover para multicanal */}
        {(() => {
          const isMulticanal = user?.role === 'channel_admin_multi' && multiChannel && multiChannel.channels.length > 1;

          // Conteúdo do branding (logo + nome) — V62: Logo aumentada para white label
          const brandingContent = sidebarOpen ? (
            <div className="flex flex-col items-center gap-3 w-full" style={{ minWidth: 0 }}>
              {brand.logoUrl ? (
                <img
                  src={brand.logoUrl}
                  alt={brand.displayName}
                  className="object-contain flex-shrink-0"
                  style={{ maxHeight: '64px', maxWidth: '120px' }}
                />
              ) : (
                <div
                  className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: accentBg }}
                >
                  <span className="font-bold text-lg" style={{ color: accentText }}>SI</span>
                </div>
              )}
              <div className="w-full text-center min-w-0">
                <span
                  title={brand.displayName}
                  className="block text-sm font-semibold line-clamp-2"
                  style={{ color: textOnSidebar }}
                >
                  {brand.displayName}
                </span>
                {isMulticanal && (
                  <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: sidebarMutedText }}>
                    <ArrowLeftRight size={12} className="flex-shrink-0" />
                    Trocar canal
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center" style={{ paddingTop: '12px', paddingBottom: '12px' }}>
              {brand.logoUrl ? (
                <img
                  src={brand.logoUrl}
                  alt={brand.displayName}
                  title={brand.displayName}
                  className="object-contain"
                  style={{ maxHeight: '48px', maxWidth: '48px' }}
                />
              ) : (
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: accentBg }}
                  title={brand.displayName}
                >
                  <span className="font-bold text-base" style={{ color: accentText }}>SI</span>
                </div>
              )}
            </div>
          );

          return (
            <div
              className="relative"
              style={{ borderBottom: `1px solid ${sidebarBorderColor}`, padding: sidebarOpen ? '14px 12px 10px' : '12px 8px' }}
            >
              {/* V61: Botão toggle sidebar — setas direcionais (recolher/expandir) */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="absolute top-2 right-2 p-1.5 rounded-md transition-colors z-10"
                style={{ color: textOnSidebar }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = sidebarBorderColor)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                title={sidebarOpen ? "Recolher menu" : "Expandir menu"}
                aria-label={sidebarOpen ? "Recolher menu" : "Expandir menu"}
              >
                {sidebarOpen ? <ChevronLeft size={18} /> : <ChevronRight size={20} />}
              </button>

              {/* V59: Se multicanal, branding é clicável e abre popover com lista de canais */}
              {isMulticanal && sidebarOpen ? (
                <Popover open={sidebarChannelOpen} onOpenChange={setSidebarChannelOpen}>
                  <PopoverTrigger asChild>
                    <div className="space-y-2">
                      <button
                        className="w-full rounded-lg transition-colors"
                        style={{ cursor: 'pointer', padding: '4px', borderRadius: '8px' }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = sidebarBorderColor)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        title="Clique para trocar de canal"
                      >
                        {brandingContent}
                      </button>
                      {/* V66: Adicionar seletor de canal clicável abaixo do branding */}
                      {multiChannel && multiChannel.channels.length >= 2 && activeChannelName && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSidebarChannelOpen(true);
                          }}
                          className="w-full text-left px-2 py-1.5 rounded-md transition-colors text-xs font-medium"
                          style={{
                            cursor: 'pointer',
                            color: accentBg,
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = sidebarBorderColor)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                          title={`Canal ativo: ${activeChannelName}. Clique para trocar.`}
                          aria-label={`Canal ativo: ${activeChannelName}. Clique para trocar.`}
                        >
                          <span className="truncate block" style={{ maxWidth: '100%' }}>
                            Canal: <span className="font-semibold">{activeChannelName}</span>
                          </span>
                        </button>
                      )}
                    </div>
                  </PopoverTrigger>
                  <PopoverContent
                    side="right"
                    align="start"
                    sideOffset={8}
                    className="w-64 p-0"
                  >
                    <div className="px-3 py-2 border-b">
                      <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                        <ArrowLeftRight className="h-3.5 w-3.5" />
                        Trocar Canal
                      </p>
                    </div>
                    <div className="py-1 max-h-64 overflow-y-auto">
                      {multiChannel!.channels.map((ch) => {
                        const isActive = ch.id === multiChannel!.activeChannelId;
                        return (
                          <button
                            key={ch.id}
                            onClick={() => {
                              if (!isActive) {
                                setActiveChannelMut.mutate({ channelId: ch.id });
                                setSidebarChannelOpen(false);
                              }
                            }}
                            className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors hover:bg-accent/50 ${
                              isActive ? 'bg-accent/30 font-medium' : ''
                            }`}
                            disabled={setActiveChannelMut.isPending}
                            title={ch.nomeFantasia || ch.name}
                          >
                            <span className="truncate">{ch.nomeFantasia || ch.name}</span>
                            {isActive && (
                              <Check className="h-4 w-4 text-green-600 flex-shrink-0 ml-2" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {setActiveChannelMut.isPending && (
                      <div className="px-3 py-2 border-t text-xs text-muted-foreground text-center">
                        Trocando canal...
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              ) : (
                <div>
                  {brandingContent}
                </div>
              )}
            </div>
          );
        })()}

        {/* Navigation */}
        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-2.5">
          {menuItems.map((item) => renderMenuNode(item))}
        </nav>

        {/* V64: Footer — e-mail + canal ativo + botão Sair (role removido) */}
        <div className="shrink-0" style={{ borderTop: `1px solid ${sidebarBorderColor}` }}>
          {/* Bloco de identificação do usuário */}
          {sidebarOpen ? (
            <div className="px-4 pt-3 pb-2 space-y-1">
              <p
                className="text-xs truncate"
                style={{ color: sidebarMutedText }}
                title={userEmail}
              >
                {userEmail}
              </p>
              {userRole === 'channel_admin_multi' && activeChannelName && (
                <p
                  className="text-[11px] font-medium truncate"
                  style={{ color: accentBg }}
                  title={activeChannelName}
                >
                  {activeChannelName}
                </p>
              )}
              {userRole === 'channel_admin_multi' && !multiChannel?.activeChannelId && (
                <p className="text-[11px] font-medium" style={{ color: '#FF6B6B' }}>
                  Selecione um canal
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-3" title={userEmail}>
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: accentBg }}
              >
                <span className="text-xs font-bold" style={{ color: accentText }}>
                  {userName.charAt(0).toUpperCase() || userEmail.charAt(0).toUpperCase() || '?'}
                </span>
              </div>
            </div>
          )}

          {/* Separador sutil */}
          <div style={{ borderTop: `1px solid ${sidebarBorderColor}`, margin: '0 16px' }} />

          {/* Botão Sair */}
          <div className="p-3">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-left"
              style={{ color: textOnSidebar }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.15)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              title={!sidebarOpen ? "Sair" : ""}
            >
              <LogOut size={18} className="flex-shrink-0" />
              {sidebarOpen && <span className="text-sm font-medium">Sair</span>}
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-border h-16 flex items-center justify-between px-6 shadow-sm">
          <h1 className="text-2xl font-bold" style={{ color: brand.primaryColor }}>{title || "Dashboard"}</h1>
          <div className="flex items-center gap-4">
            <DashboardModeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 rounded-lg hover:bg-accent/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <div className="text-right">
                    <p className="text-sm font-semibold text-foreground">{userName}</p>
                    <p className="text-xs text-muted-foreground">{userEmail}</p>
                  </div>
                  <Avatar className="h-10 w-10" style={{ borderColor: brand.secondaryColor, borderWidth: "2px" }}>
                    <AvatarFallback
                      className="font-bold"
                      style={{ backgroundColor: brand.secondaryColor, color: brand.textOnSecondary }}
                    >
                      {userName.charAt(0).toUpperCase() || userEmail.charAt(0).toUpperCase() || '?'}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                {/* V56: Channel switcher for multicanal users */}
                {user?.role === 'channel_admin_multi' && multiChannel && multiChannel.channels.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground flex items-center gap-1">
                      <ArrowLeftRight className="h-3 w-3" />
                      Trocar Canal
                    </div>
                    {multiChannel.channels.map((ch) => (
                      <DropdownMenuItem
                        key={ch.id}
                        onClick={() => {
                          if (ch.id !== multiChannel.activeChannelId) {
                            setActiveChannelMut.mutate({ channelId: ch.id });
                          }
                        }}
                        className={`cursor-pointer flex items-center justify-between ${
                          ch.id === multiChannel.activeChannelId ? 'bg-accent/50' : ''
                        }`}
                      >
                        <span className="truncate">{ch.nomeFantasia || ch.name}</span>
                        {ch.id === multiChannel.activeChannelId && (
                          <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                        )}
                      </DropdownMenuItem>
                    ))}
                    <div className="border-t border-border my-1" />
                  </>
                )}
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sair</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-auto bg-[#F5F5F5]">
          {portalContext === 'nucleo' && (() => {
            const contextChips = ['Painel Administrativo', 'Contratos', 'Catálogo de Serviços', 'Usuários e Perfis'];

            return (
              <div className="border-b border-slate-200 bg-white/90 px-6 py-4 backdrop-blur-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Administração da Plataforma</p>
                    <p className="mt-1 text-sm text-slate-600">Você está no backoffice estrutural do PJC, responsável por canais, clientes, contratos, catálogo e perfis.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {contextChips.map((chip: string) => (
                      <div key={chip} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600">
                        {chip}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
          <div className="p-6">{children}</div>
          <ModuleAIChatWidget enabled={canRenderModuleAIWidget} />
        </main>
      </div>
    </div>
  );
}
